import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';

const ROLE_CONFIG = {
  superadmin:     { color: 'danger',  icon: 'bi-shield-fill-exclamation', label: 'Super Admin'     },
  security_head:  { color: 'dark',    icon: 'bi-shield-fill-check',       label: 'Security Head'   },
  admin_head:     { color: 'secondary',icon: 'bi-shield-fill',            label: 'Admin Head'      },
  location_admin: { color: 'info',    icon: 'bi-geo-alt-fill',            label: 'Location Admin'  },
  guard:          { color: 'warning', icon: 'bi-shield-lock-fill',        label: 'Reception'       },
  employee:       { color: 'primary', icon: 'bi-person-fill',             label: 'Employee'        },
};

const INITIAL_FORM = {
  name: '', email: '', password: '', role: 'employee',
  phone: '', department: '', office: '', ssoOnly: false,
};

// Roles that require an office assignment
const NEEDS_OFFICE = ['location_admin', 'guard', 'employee'];

const Users = () => {
  const { user: me, hasRole } = useAuth();
  const isSuperAdmin = hasRole('superadmin');

  const [users,     setUsers]     = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [offices,   setOffices]   = useState([]);
  const [search,    setSearch]    = useState('');
  const [roleFilter,setRoleFilter]= useState('');
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget,setEditTarget]= useState(null);
  const [form,      setForm]      = useState(INITIAL_FORM);
  const [saving,       setSaving]       = useState(false);
  const [resetTarget,  setResetTarget]  = useState(null);  // user to reset password for
  const [newPassword,  setNewPassword]  = useState('');
  const [resetting,    setResetting]    = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/auth/users');
      setUsers(data.users);
    } catch { toast.error('Failed to load users'); }
    finally   { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    api.get('/offices').then(({ data }) => setOffices(data.offices || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let result = users;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(u =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.department?.toLowerCase().includes(q) ||
        u.phone?.includes(q) ||
        u.role?.toLowerCase().includes(q) ||
        u.office?.name?.toLowerCase().includes(q)
      );
    }
    if (roleFilter) result = result.filter(u => u.role === roleFilter);
    setFiltered(result);
  }, [search, roleFilter, users]);

  const openCreate = () => { setEditTarget(null); setForm(INITIAL_FORM); setShowModal(true); };
  const openEdit   = (u) => {
    setEditTarget(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role, phone: u.phone || '', department: u.department || '', office: u.office?._id || '' });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { ssoOnly, ...rest } = form;
      const payload = { ...rest };
      if (!payload.office)    delete payload.office;
      // SSO-only users have no password — strip it out
      if (ssoOnly || !payload.password) delete payload.password;
      if (editTarget) {
        delete payload.email;
        await api.patch(`/auth/users/${editTarget._id}`, payload);
        toast.success('User updated');
      } else {
        await api.post('/auth/register', payload);
        toast.success('User created');
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setResetting(true);
    try {
      await api.patch(`/auth/users/${resetTarget._id}/reset-password`, { newPassword });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null); setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.message || 'Reset failed'); }
    finally { setResetting(false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Permanently delete "${u.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/auth/users/${u._id}`);
      toast.success(`${u.name} deleted`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/auth/users/${u._id}`, { active: !u.active });
      toast.success(`User ${u.active ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch { toast.error('Failed to update status'); }
  };

  const roleCounts = users.reduce((acc, u) => { acc[u.role] = (acc[u.role] || 0) + 1; return acc; }, {});

  // Roles available to create (superadmin sees all; location_admin sees only guard/employee)
  const creatableRoles = isSuperAdmin
    ? Object.keys(ROLE_CONFIG)
    : ['guard', 'employee'];

  return (
    <div className="container-fluid py-4 px-3 px-lg-4">
      {/* Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-0">User Management</h4>
          <p className="text-muted small mb-0">Manage portal users across all roles and offices</p>
        </div>
        <button className="btn btn-primary fw-semibold" onClick={openCreate}>
          <i className="bi bi-person-plus me-2"></i>Add User
        </button>
      </div>

      {/* Role summary chips */}
      <div className="row g-3 mb-4">
        {Object.entries(ROLE_CONFIG).filter(([r]) => roleCounts[r]).map(([role, cfg]) => (
          <div key={role} className="col-6 col-lg-3 col-xl-2">
            <div
              className={`card border-0 shadow-sm rounded-4 cursor-pointer ${roleFilter === role ? 'ring-primary' : ''}`}
              style={{ borderLeft: `4px solid var(--bs-${cfg.color})`, cursor: 'pointer' }}
              onClick={() => setRoleFilter(roleFilter === role ? '' : role)}
            >
              <div className="card-body p-3 d-flex align-items-center gap-2">
                <i className={`bi ${cfg.icon} text-${cfg.color} fs-5`}></i>
                <div>
                  <div className={`fs-4 fw-bold text-${cfg.color} lh-1`}>{roleCounts[role] || 0}</div>
                  <div className="small text-muted" style={{ fontSize: '0.7rem' }}>{cfg.label}s</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search + filters */}
      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-5">
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0"><i className="bi bi-search text-muted"></i></span>
                <input type="text" className="form-control border-start-0 ps-0" placeholder="Search name, email, department, office…"
                  value={search} onChange={e => setSearch(e.target.value)} />
                {search && <button className="btn btn-outline-secondary" onClick={() => setSearch('')}><i className="bi bi-x"></i></button>}
              </div>
            </div>
            <div className="col-6 col-md-3">
              <select className="form-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                <option value="">All Roles</option>
                {Object.entries(ROLE_CONFIG).map(([r, c]) => <option key={r} value={r}>{c.label}</option>)}
              </select>
            </div>
            <div className="col-6 col-md-4 text-muted small">
              Showing <strong>{filtered.length}</strong> of <strong>{users.length}</strong> users
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-person-x fs-1 d-block opacity-25 mb-2"></i>No users found
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4 py-3">User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Office</th>
                    <th className="d-none d-md-table-cell">Department</th>
                    <th className="d-none d-lg-table-cell">Phone</th>
                    <th className="d-none d-lg-table-cell">Created</th>
                    <th>Status</th>
                    <th className="pe-4 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => {
                    const cfg = ROLE_CONFIG[u.role] || ROLE_CONFIG.employee;
                    return (
                      <tr key={u._id} className={!u.active ? 'opacity-50' : ''}>
                        <td className="ps-4">
                          <div className="d-flex align-items-center gap-2">
                            <div className={`rounded-circle bg-${cfg.color} bg-opacity-10 d-flex align-items-center justify-content-center fw-bold text-${cfg.color} flex-shrink-0`}
                              style={{ width: 34, height: 34, fontSize: 13 }}>
                              {u.name?.[0]?.toUpperCase()}
                            </div>
                            <div>
                              <div className="fw-semibold lh-1 d-flex align-items-center gap-1 flex-wrap">
                                {u.name}
                                {u._id === me?._id && <span className="badge bg-primary" style={{ fontSize: '0.55rem' }}>You</span>}
                                {!u.hasPassword && <span className="badge" style={{ background: '#e3f0fb', color: '#0078d4', fontSize: '0.55rem' }}><i className="bi bi-microsoft me-1"></i>SSO</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="text-muted">{u.email}</td>
                        <td>
                          <span className={`badge bg-${cfg.color} bg-opacity-10 text-${cfg.color} fw-semibold`} style={{ fontSize: '0.68rem' }}>
                            <i className={`bi ${cfg.icon} me-1`}></i>{cfg.label}
                          </span>
                        </td>
                        <td>
                          {u.office
                            ? <>
                                <div className="fw-semibold lh-1" style={{ fontSize: '0.78rem' }}>{u.office.name}</div>
                                <div className="text-muted font-monospace" style={{ fontSize: '0.65rem' }}>{u.office.code}</div>
                              </>
                            : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-muted d-none d-md-table-cell">{u.department || '—'}</td>
                        <td className="font-monospace text-muted d-none d-lg-table-cell">{u.phone || '—'}</td>
                        <td className="text-muted d-none d-lg-table-cell">{formatDate(u.createdAt)}</td>
                        <td>
                          <span className={`badge rounded-pill ${u.active ? 'bg-success' : 'bg-secondary'}`}>
                            {u.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="pe-4 text-end">
                          <div className="d-flex gap-1 justify-content-end">
                            <button className="btn btn-sm btn-outline-primary px-2" onClick={() => openEdit(u)} title="Edit">
                              <i className="bi bi-pencil"></i>
                            </button>
                            {u.hasPassword && (
                              <button className="btn btn-sm btn-outline-secondary px-2" onClick={() => { setResetTarget(u); setNewPassword(''); }} title="Reset Password">
                                <i className="bi bi-key-fill"></i>
                              </button>
                            )}
                            {u._id !== me?._id && (
                              <button
                                className={`btn btn-sm px-2 ${u.active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                onClick={() => toggleActive(u)}
                                title={u.active ? 'Deactivate' : 'Activate'}
                              >
                                <i className={`bi ${u.active ? 'bi-person-slash' : 'bi-person-check'}`}></i>
                              </button>
                            )}
                            {isSuperAdmin && u._id !== me?._id && (
                              <button className="btn btn-sm btn-outline-danger px-2" onClick={() => handleDelete(u)} title="Delete">
                                <i className="bi bi-trash3"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <form className="modal-content rounded-4 border-0 shadow-lg" onSubmit={handleSave}>
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="modal-title fw-bold">
                  <i className={`bi ${editTarget ? 'bi-pencil-square' : 'bi-person-plus'} text-primary me-2`}></i>
                  {editTarget ? 'Edit User' : 'Create New User'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body px-4 py-3">
                {/* Hidden dummy fields trick browsers into not autofilling the real fields */}
                <input type="text" style={{ display: 'none' }} autoComplete="username" readOnly />
                <input type="password" style={{ display: 'none' }} autoComplete="new-password" readOnly />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Full Name *</label>
                    <input type="text" name="name" className="form-control" value={form.name} onChange={handleChange} required autoComplete="off" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Email *</label>
                    <input type="email" name="email" className="form-control" value={form.email} onChange={handleChange} required disabled={!!editTarget} autoComplete="off" />
                  </div>
                  {/* SSO toggle — shown only when creating a new user */}
                  {!editTarget && (
                    <div className="col-12">
                      <div
                        className="d-flex align-items-center gap-3 p-3 rounded-3"
                        style={{ background: form.ssoOnly ? '#e8f5ee' : '#f8f9fa', border: `1px solid ${form.ssoOnly ? '#b2dfcc' : '#dee2e6'}`, transition: 'all .2s' }}
                      >
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="ssoOnlySwitch"
                            name="ssoOnly"
                            checked={form.ssoOnly}
                            onChange={handleChange}
                            style={{ width: '2.4em', height: '1.3em', cursor: 'pointer' }}
                          />
                        </div>
                        <label htmlFor="ssoOnlySwitch" className="mb-0" style={{ cursor: 'pointer' }}>
                          <div className="fw-semibold small d-flex align-items-center gap-2">
                            <i className="bi bi-microsoft" style={{ color: '#0078d4' }}></i>
                            SSO / Office 365 login only
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            {form.ssoOnly
                              ? 'User will sign in with their Microsoft Office 365 account — no password needed.'
                              : 'Toggle on if this user will log in via Microsoft Office 365.'}
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Password — hidden when SSO-only */}
                  {!editTarget && !form.ssoOnly && (
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Password *</label>
                      <input type="password" name="password" className="form-control" value={form.password} onChange={handleChange} required minLength={6} placeholder="Min 6 characters" autoComplete="new-password" />
                    </div>
                  )}
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Role *</label>
                    <select name="role" className="form-select" value={form.role} onChange={handleChange}>
                      {creatableRoles.map(r => (
                        <option key={r} value={r}>{ROLE_CONFIG[r]?.label || r}</option>
                      ))}
                    </select>
                  </div>
                  {NEEDS_OFFICE.includes(form.role) && (
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Office {NEEDS_OFFICE.includes(form.role) ? '*' : ''}</label>
                      <select name="office" className="form-select" value={form.office} onChange={handleChange} required={NEEDS_OFFICE.includes(form.role)}>
                        <option value="">Select office…</option>
                        {offices.map(o => <option key={o._id} value={o._id}>{o.name} ({o.code})</option>)}
                      </select>
                    </div>
                  )}
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Phone</label>
                    <input type="tel" name="phone" className="form-control" value={form.phone} onChange={handleChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Department</label>
                    <input type="text" name="department" className="form-control" value={form.department} onChange={handleChange} />
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button type="button" className="btn btn-light rounded-3" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary rounded-3 fw-semibold px-4" disabled={saving}>
                  {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : (editTarget ? 'Save Changes' : 'Create User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Reset Password Modal ── */}
      {resetTarget && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content border-0 shadow rounded-4">
              <div className="modal-header border-0 px-4 pt-4 pb-2">
                <div>
                  <h5 className="modal-title fw-bold mb-1">Reset Password</h5>
                  <p className="text-muted small mb-0">Set a new password for <strong>{resetTarget.name}</strong></p>
                </div>
                <button className="btn-close" onClick={() => { setResetTarget(null); setNewPassword(''); }}></button>
              </div>
              <div className="modal-body px-4 py-3">
                <label className="form-label small fw-semibold">New Password</label>
                <input
                  type="password"
                  className="form-control rounded-3"
                  placeholder="Min. 6 characters"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleResetPassword(); }}
                  autoFocus
                />
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button className="btn btn-light rounded-3" onClick={() => { setResetTarget(null); setNewPassword(''); }}>Cancel</button>
                <button className="btn btn-warning rounded-3 fw-semibold px-4" onClick={handleResetPassword} disabled={resetting}>
                  {resetting ? <><span className="spinner-border spinner-border-sm me-2"></span>Resetting…</> : <><i className="bi bi-key-fill me-2"></i>Reset Password</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
